import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import spformsApi from '../lib/spformsApi';

const {
  FiX,
  FiSend,
  FiMail,
  FiUsers,
  FiUser,
  FiLoader,
  FiCheckCircle,
  FiAlertCircle,
  FiFileText,
} = FiIcons;

const DEFAULT_BODY = `Καλησπέρα σας,

Σας ενημερώνουμε ότι η συμμετοχή του/της {{participant_name}} για το {{event_title}} θεωρείται έγκυρη με την αποστολή του αποδεικτικού πληρωμής συμμετοχής στο {{tenant_email}}.

Παρακαλούμε στο θέμα του email να αναγράφεται:

{{payment_email_subject}} – {{participant_name}}

Ευχαριστούμε πολύ,
{{tenant_name}}`;

function renderPreview(text, variables) {
  let output = String(text || '');

  Object.entries(variables || {}).forEach(([key, value]) => {
    const pattern = new RegExp(`{{\\s*${key}\\s*}}`, 'gi');
    output = output.replace(pattern, value || '');
  });

  return output;
}

function getParticipantName(participant) {
  return (
    participant?.participant_name ||
    participant?.full_name ||
    participant?.name ||
    `${participant?.first_name || ''} ${participant?.last_name || ''}`.trim() ||
    `${participant?.parent_name || ''}`.trim() ||
    'Participant'
  );
}

function getParticipantEmail(participant) {
  return (
    participant?.email ||
    participant?.parent_email ||
    participant?.guardian_email ||
    participant?.contact_email ||
    ''
  );
}

export default function EmailMessageModal({
  open,
  onClose,
  token,
  tenant,
  intakeGroup,
  participant = null,
  participants = [],
  mode = 'single',
  onSent,
}) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [subject, setSubject] = useState('Οδηγίες επιβεβαίωσης συμμετοχής');
  const [body, setBody] = useState(DEFAULT_BODY);
  const [paymentEmailSubject, setPaymentEmailSubject] = useState('Συμμετοχή Tryouts 26');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const isBulk = mode === 'bulk';

  const activeRecipients = useMemo(() => {
    if (isBulk) {
      return Array.isArray(participants) ? participants.filter((p) => getParticipantEmail(p)) : [];
    }

    return participant && getParticipantEmail(participant) ? [participant] : [];
  }, [isBulk, participants, participant]);

  const previewVariables = useMemo(() => {
    const sample = activeRecipients[0] || participant || {};
    const participantName = getParticipantName(sample);

    return {
      participant_name: participantName,
      first_name: String(participantName).split(/\s+/)[0] || '',
      last_name: String(participantName).split(/\s+/).slice(1).join(' ') || '',
      event_title: intakeGroup?.title || '',
      event_start_date: intakeGroup?.start_date || '',
      event_end_date: intakeGroup?.end_date || '',
      event_location: intakeGroup?.location || '',
      tenant_name: tenant?.name || tenant?.tenant_name || '',
      tenant_email: tenant?.contact_email || tenant?.email || '',
      payment_email_subject: paymentEmailSubject,
      amount_due: sample?.amount_due ?? '',
      amount_paid: sample?.amount_paid ?? '',
      outstanding_balance:
        sample?.outstanding_balance ??
        sample?.remaining_balance ??
        '',
    };
  }, [activeRecipients, participant, intakeGroup, tenant, paymentEmailSubject]);

  useEffect(() => {
    if (!open || !token || !tenant?.id) return;

    let alive = true;

    async function loadTemplates() {
      try {
        setLoadingTemplates(true);
        setError('');

        const data = await spformsApi.emailTemplatesList(
          token,
          tenant.id,
          intakeGroup?.id || null
        );

        if (!alive) return;
        setTemplates(data || []);

        const first = (data || []).find((item) => item.active !== false);
        if (first) {
          setSelectedTemplateId(first.id);
          setSubject(first.subject || 'Οδηγίες επιβεβαίωσης συμμετοχής');
          setBody(first.body || DEFAULT_BODY);
        }
      } catch (err) {
        if (!alive) return;
        console.error(err);
        setError(err?.message || 'Failed to load email templates');
      } finally {
        if (alive) setLoadingTemplates(false);
      }
    }

    loadTemplates();

    return () => {
      alive = false;
    };
  }, [open, token, tenant?.id, intakeGroup?.id]);

  function handleTemplateChange(templateId) {
    setSelectedTemplateId(templateId);

    const selected = templates.find((item) => item.id === templateId);
    if (!selected) return;

    setSubject(selected.subject || '');
    setBody(selected.body || '');
  }

  async function handleSend() {
    try {
      setSending(true);
      setError('');
      setResult(null);

      if (!subject.trim()) throw new Error('Subject is required');
      if (!body.trim()) throw new Error('Message body is required');
      if (activeRecipients.length === 0) throw new Error('No recipient email found');

      if (isBulk) {
        const response = await spformsApi.sendBulkCustomEmail({
          tenant_id: tenant?.id || null,
          tenant_name: tenant?.name || tenant?.tenant_name || '',
          tenant_email: tenant?.contact_email || tenant?.email || '',
          intake_group_id: intakeGroup?.id || null,
          public_slug: intakeGroup?.public_slug || '',
          group: intakeGroup || {},
          custom_subject: subject,
          custom_body: body,
          payment_email_subject: paymentEmailSubject,
          recipients: activeRecipients.map((p) => ({
            email: getParticipantEmail(p),
            participant_name: getParticipantName(p),
            participant_id: p.participant_id || p.id || null,
            registration_id: p.registration_id || null,
            core: p,
            answers: p.answers || {},
          })),
        });

        setResult(response);
        if (onSent) onSent(response);
        return;
      }

      const p = activeRecipients[0];

      const response = await spformsApi.sendCustomEmail({
        tenant_id: tenant?.id || null,
        tenant_name: tenant?.name || tenant?.tenant_name || '',
        tenant_email: tenant?.contact_email || tenant?.email || '',
        intake_group_id: intakeGroup?.id || null,
        public_slug: intakeGroup?.public_slug || '',
        group: intakeGroup || {},
        recipient_email: getParticipantEmail(p),
        participant_name: getParticipantName(p),
        participant_id: p.participant_id || p.id || null,
        registration_id: p.registration_id || null,
        core: p,
        answers: p.answers || {},
        custom_subject: subject,
        custom_body: body,
        payment_email_subject: paymentEmailSubject,
        amount_due: p.amount_due || 0,
        amount_paid: p.amount_paid || 0,
        remaining_balance: p.remaining_balance || p.outstanding_balance || 0,
        outstanding_balance: p.outstanding_balance || p.remaining_balance || 0,
        payment_status: p.payment_status || '',
        payment_gateway: p.payment_gateway || '',
        discount_code: p.discount_code || null,
      });

      setResult(response);
      if (onSent) onSent(response);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Email failed');
    } finally {
      setSending(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <SafeIcon icon={isBulk ? FiUsers : FiMail} />
              {isBulk ? 'Bulk Message' : 'Send Message'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {isBulk
                ? `${activeRecipients.length} recipients with email`
                : getParticipantEmail(participant) || 'No email found'}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-xl hover:bg-gray-100 flex items-center justify-center"
          >
            <SafeIcon icon={FiX} className="text-gray-500" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 max-h-[calc(92vh-82px)] overflow-y-auto">
          <div className="p-6 border-r border-gray-200 space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
                <SafeIcon icon={FiAlertCircle} className="text-red-600 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {result && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex gap-3">
                <SafeIcon icon={FiCheckCircle} className="text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-green-800">
                    Email sent successfully
                  </p>
                  {isBulk && (
                    <p className="text-xs text-green-700 mt-1">
                      Sent: {result.sent || 0} / Failed: {result.failed || 0}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template
              </label>
              <div className="relative">
                <select
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  disabled={loadingTemplates}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">
                    {loadingTemplates ? 'Loading templates...' : 'Manual message'}
                  </option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment email subject variable
              </label>
              <input
                value={paymentEmailSubject}
                onChange={(e) => setPaymentEmailSubject(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Συμμετοχή Tryouts 26"
              />
              <p className="text-xs text-gray-500 mt-1">
                Used as {'{{payment_email_subject}}'}.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Email subject"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message body
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={13}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                placeholder="Write message..."
              />
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <SafeIcon icon={FiFileText} />
                Available variables
              </p>
              <div className="flex flex-wrap gap-2 text-xs">
                {[
                  '{{participant_name}}',
                  '{{first_name}}',
                  '{{last_name}}',
                  '{{event_title}}',
                  '{{event_start_date}}',
                  '{{event_end_date}}',
                  '{{event_location}}',
                  '{{tenant_name}}',
                  '{{tenant_email}}',
                  '{{payment_email_subject}}',
                  '{{amount_due}}',
                  '{{amount_paid}}',
                  '{{outstanding_balance}}',
                ].map((variable) => (
                  <span
                    key={variable}
                    className="px-2 py-1 rounded-lg bg-white border border-gray-200 text-gray-700"
                  >
                    {variable}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={sending}
                className="px-5 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSend}
                disabled={sending || activeRecipients.length === 0}
                className="px-5 py-3 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2"
              >
                <SafeIcon icon={sending ? FiLoader : FiSend} className={sending ? 'animate-spin' : ''} />
                {sending ? 'Sending...' : isBulk ? 'Send Bulk Message' : 'Send Message'}
              </button>
            </div>
          </div>

          <div className="p-6 bg-gray-50">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  Preview
                </p>
                <h3 className="text-lg font-bold text-gray-900 mt-1">
                  {renderPreview(subject, previewVariables)}
                </h3>
              </div>

              <div className="p-5">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <SafeIcon icon={isBulk ? FiUsers : FiUser} className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {isBulk ? `${activeRecipients.length} recipients` : getParticipantName(activeRecipients[0])}
                    </p>
                    <p className="text-xs text-gray-500">
                      {isBulk ? 'Bulk email preview uses first recipient data' : getParticipantEmail(activeRecipients[0])}
                    </p>
                  </div>
                </div>

                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap leading-7">
                  {renderPreview(body, previewVariables)}
                </div>
              </div>
            </div>

            {isBulk && activeRecipients.length > 0 && (
              <div className="mt-5 bg-white border border-gray-200 rounded-2xl p-5">
                <p className="text-sm font-semibold text-gray-700 mb-3">
                  Recipients preview
                </p>
                <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                  {activeRecipients.slice(0, 20).map((p, index) => (
                    <div key={p.id || p.participant_id || index} className="py-2">
                      <p className="text-sm font-medium text-gray-900">
                        {getParticipantName(p)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {getParticipantEmail(p)}
                      </p>
                    </div>
                  ))}
                </div>
                {activeRecipients.length > 20 && (
                  <p className="text-xs text-gray-500 mt-3">
                    + {activeRecipients.length - 20} more recipients
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}